import { useState } from 'react';
import type { ClientNotificationSettings } from '@lex/contracts';
import { apiClient } from '../../lib/api';
import { useAuth } from '../../lib/auth';

type ContactKind = 'phone' | 'email';

export function ProfileSecurity() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);

  const [kind, setKind] = useState<ContactKind | null>(null);
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'value' | 'code'>('value');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const notifications: ClientNotificationSettings = user?.notifications ?? {
    telegram: true,
    email: true,
    lawyerAssigned: true,
    answerReady: true,
    deadline: true,
  };

  const saveName = async (): Promise<void> => {
    setNameBusy(true);
    setNameSaved(false);
    try {
      await apiClient.auth.updateProfile({ name: name.trim() });
      await refresh();
      setNameSaved(true);
    } finally {
      setNameBusy(false);
    }
  };

  const toggle = async (key: keyof ClientNotificationSettings): Promise<void> => {
    await apiClient.auth.updateNotifications({ [key]: !notifications[key] });
    await refresh();
  };

  const openContact = (next: ContactKind): void => {
    setKind(next);
    setStep('value');
    setValue(next === 'phone' ? (user?.phone ?? '') : (user?.email ?? ''));
    setCode('');
    setError(null);
    setNotice(null);
  };

  const requestCode = async (): Promise<void> => {
    if (!kind || !value.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'phone') await apiClient.auth.requestPhoneChange(value.trim());
      else await apiClient.auth.requestEmailChange(value.trim());
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить код');
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (): Promise<void> => {
    if (!kind) return;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'phone') await apiClient.auth.confirmPhoneChange(value.trim(), code);
      else await apiClient.auth.confirmEmailChange(value.trim(), code);
      await refresh();
      setNotice(kind === 'phone' ? 'Номер изменён' : 'Почта подтверждена');
      setKind(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неверный код');
    } finally {
      setBusy(false);
    }
  };

  const scheduleDeletion = async (): Promise<void> => {
    setDeleteBusy(true);
    try {
      await apiClient.auth.requestDeletion();
      await refresh();
      setDeleting(false);
      setNotice('Удаление запланировано через 24 часа');
    } finally {
      setDeleteBusy(false);
    }
  };

  const cancelDeletion = async (): Promise<void> => {
    setDeleteBusy(true);
    try {
      await apiClient.auth.cancelDeletion();
      await refresh();
      setNotice('Удаление отменено');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-ink">Профиль и безопасность</h2>
      <div className="space-y-4 rounded-2xl border border-line bg-white/90 p-5">
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium text-ink">Имя</span>
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="w-full rounded-xl border border-line px-3 py-2.5 text-ink outline-none focus:border-accent"
            />
            <button
              type="button"
              disabled={nameBusy || !name.trim()}
              onClick={() => void saveName()}
              className="shrink-0 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              Сохранить
            </button>
          </div>
          {nameSaved ? <span className="text-xs text-success">Сохранено</span> : null}
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-line px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
              Телефон
            </div>
            <div className="mt-1 text-[14.5px] text-ink">{user?.phone ?? 'не указан'}</div>
            <button
              type="button"
              onClick={() => openContact('phone')}
              className="mt-1 text-[12px] font-semibold text-accent"
            >
              Изменить
            </button>
          </div>
          <div className="rounded-xl border border-line px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
              Почта
            </div>
            <div className="mt-1 text-[14.5px] text-ink">{user?.email ?? 'не указана'}</div>
            <button
              type="button"
              onClick={() => openContact('email')}
              className="mt-1 text-[12px] font-semibold text-accent"
            >
              {user?.email ? 'Изменить' : 'Добавить'}
            </button>
          </div>
        </div>

        <div className="rounded-xl bg-soft px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-[14px] font-semibold text-ink">Telegram</div>
              <div className="text-[12px] text-muted">
                {user?.telegramId ? 'Подключён для уведомлений' : 'Не подключён'}
              </div>
            </div>
            <span
              className={`rounded-lg px-2.5 py-1 text-[12px] font-semibold ${
                user?.telegramId ? 'bg-success-soft text-success' : 'bg-white text-muted'
              }`}
            >
              {user?.telegramId ? 'Подключён' : 'Не подключён'}
            </span>
          </div>
        </div>

        <div>
          <div className="mb-2 text-[13px] font-semibold text-muted">Уведомления</div>
          <div className="space-y-1.5">
            {(
              [
                ['lawyerAssigned', 'Юрист назначен'],
                ['answerReady', 'Ответ юриста готов'],
                ['deadline', 'Напоминание о сроке'],
                ['telegram', 'Канал Telegram'],
                ['email', 'Канал Email'],
              ] as Array<[keyof ClientNotificationSettings, string]>
            ).map(([key, label]) => (
              <label
                key={key}
                className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-sm text-ink"
              >
                <span>{label}</span>
                <input
                  type="checkbox"
                  checked={notifications[key]}
                  onChange={() => void toggle(key)}
                />
              </label>
            ))}
          </div>
        </div>

        {notice ? <p className="text-[13px] font-medium text-success">{notice}</p> : null}

        <div className="border-t border-line pt-4">
          <div className="text-[14px] font-semibold text-ink">Удаление аккаунта</div>
          {user?.deletionScheduledAt ? (
            <div className="mt-2 rounded-xl bg-[#FBF3DE] px-4 py-3 text-[13px] text-[#7A5C14]">
              Удаление запланировано на{' '}
              {new Date(user.deletionScheduledAt).toLocaleString('ru-RU')}. До этого момента
              аккаунт можно восстановить.
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => void cancelDeletion()}
                className="ml-2 font-semibold text-accent"
              >
                Отменить
              </button>
            </div>
          ) : (
            <>
              <p className="mt-1 text-[12.5px] leading-5 text-muted">
                Через 24 часа удалим переписку, файлы и личные данные. История обращений
                сохранится обезличенной.
              </p>
              {!deleting ? (
                <button
                  type="button"
                  onClick={() => setDeleting(true)}
                  className="mt-2 text-[13px] font-semibold text-danger"
                >
                  Удалить аккаунт
                </button>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={deleteBusy}
                    onClick={() => void scheduleDeletion()}
                    className="rounded-xl bg-danger px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Подтвердить удаление
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(false)}
                    className="rounded-xl border border-line px-4 py-2 text-sm font-semibold text-ink"
                  >
                    Отмена
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {kind ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl border border-white/80 bg-white p-6 shadow-chat">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink">
                {kind === 'phone' ? 'Смена номера' : 'Смена почты'}
              </h3>
              <button type="button" onClick={() => setKind(null)} className="text-muted">
                ×
              </button>
            </div>
            {step === 'value' ? (
              <>
                <label className="block space-y-1.5 text-sm">
                  <span className="font-medium text-ink">
                    {kind === 'phone' ? 'Новый номер' : 'Новая почта'}
                  </span>
                  <input
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    placeholder={kind === 'phone' ? '+7 999 123-45-67' : 'you@example.com'}
                    className="w-full rounded-xl border border-line px-3 py-2.5 text-ink"
                  />
                </label>
                {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
                <button
                  type="button"
                  disabled={busy || !value.trim()}
                  onClick={() => void requestCode()}
                  className="mt-4 w-full rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  Получить код
                </button>
              </>
            ) : (
              <>
                <p className="text-[13px] text-muted">
                  Код отправлен {kind === 'phone' ? 'по SMS' : 'на почту'} на {value}.
                </p>
                <input
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  inputMode="numeric"
                  placeholder="Код из сообщения"
                  className="mt-3 w-full rounded-xl border border-line px-3 py-2.5 text-ink"
                />
                {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
                <button
                  type="button"
                  disabled={busy || !code.trim()}
                  onClick={() => void confirmCode()}
                  className="mt-4 w-full rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  Подтвердить
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
